import React, { useRef, useEffect, useState } from 'react';

const EmailIframe = ({ emailBody }) => {
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState('0px');

  // The script to be injected into the HTML to send the document's height
  const injectedScript = `
    <script>
      (function(){
        function sendHeight() {
          if (!document.body) return;
          // By strictly measuring the body's scroll height (and ensuring body doesn't stretch),
          // we get the true content height without it snapping to the viewport size.
          var height = document.body.scrollHeight;
          window.parent.postMessage({ type: 'setHeight', height: height }, '*');
        }
        window.addEventListener('load', sendHeight);
        if (window.ResizeObserver) {
          var ro = new ResizeObserver(sendHeight);
          ro.observe(document.documentElement);
          if (document.body) ro.observe(document.body);
        } else {
          var observer = new MutationObserver(sendHeight);
          observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true });
        }
      })();
    </script>
  `;

  // Inject default styles to ensure readability, especially for transparent emails
  const defaultStyles = `
    <style>
      html, body {
        background-color: #f4f4f5 !important;
        color: #18181b !important;
        height: auto !important;
        min-height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
        font-size: 15px !important;
        line-height: 1.65 !important;
        -webkit-font-smoothing: antialiased;
      }
      body {
        padding: 20px 24px !important; /* Comfortable internal reading padding */
      }
      a {
        color: #4f46e5 !important;
      }
      p, div, span, li, td {
        font-size: inherit;
        line-height: inherit;
      }
    </style>
  `;

  // Check if emailBody has any common HTML tags
  const containsHtml = /<[a-z][\s\S]*>/i.test(emailBody);

  let formattedBody = emailBody;
  if (!containsHtml) {
    // Wrap plain text in a pre-wrap container to preserve newlines
    formattedBody = `<div style="white-space: pre-wrap; font-family: inherit;">${emailBody}</div>`;
  }

  // Check if formattedBody already has <html> and <body> tags
  let completeHtml;
  const hasHtmlTag =
    /<html[\s\S]*>/i.test(formattedBody) && /<\/html>/i.test(formattedBody);
  const hasBodyTag =
    /<body[\s\S]*>/i.test(formattedBody) && /<\/body>/i.test(formattedBody);

  if (hasHtmlTag && hasBodyTag) {
    completeHtml = formattedBody;
    // Inject default styles into the head if possible
    if (/<head[\s\S]*>/i.test(completeHtml)) {
      completeHtml = completeHtml.replace(
        /(<head[\s\S]*?>)/i,
        '$1' + defaultStyles,
      );
    } else {
      completeHtml = completeHtml.replace(
        /(<html[\s\S]*?>)/i,
        '$1<head>' + defaultStyles + '</head>',
      );
    }
  } else {
    // Wrap the content in a complete HTML document
    completeHtml = `
      <html>
        <head>
          <meta charset="utf-8">
          <title>Email Content</title>
          ${defaultStyles}
        </head>
        <body>
          ${formattedBody}
        </body>
      </html>
    `;
  }

  // Insert the script before the closing </body> tag, if it exists
  let modifiedEmailBody;
  if (/<\/body>/i.test(completeHtml)) {
    modifiedEmailBody = completeHtml.replace(
      /<\/body>/i,
      injectedScript + '</body>',
    );
  } else {
    modifiedEmailBody = completeHtml + injectedScript;
  }

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'setHeight') {
        setIframeHeight(event.data.height + 'px');
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      title="Email Content"
      srcDoc={modifiedEmailBody}
      style={{
        width: '100%',
        height: iframeHeight !== '0px' ? iframeHeight : 'auto',
        minHeight: '120px',
        border: 'none',
        borderRadius: '12px',
        backgroundColor: '#f4f4f5',
        display: 'block',
      }}
      sandbox="allow-popups allow-scripts"
      scrolling="no"
    />
  );
};

export default EmailIframe;
